import { type ComponentProps, ReactNode } from 'react';
import Head from 'next/head';
import { Layout as SfNextLayout } from '@stitch-fix/sf-next';
import { ResponsiveSection } from '@stitch-fix/mode-react';
import PageHero, { PageHeroProps } from '../PageHero';
import PageFooter from '../PageFooter';
import Breadcrumbs, { BreadcrumbsProps } from '../Breadcrumbs';
import styles from './styles.module.scss';
/**
 * AppLayout is a wrapper for the sf-next `Layout` component that provides an api
 * to pass `Head` title and meta tag data. This will help provide a consistent
 * api for all supported meta tags for other pages in the future.
 */

interface AppLayoutProps {
  children: ReactNode;
  description?: string;
  title?: string;
  hero?: PageHeroProps;
  breadcrumbs?: BreadcrumbsProps['breadcrumbs'];
  header?: ComponentProps<typeof SfNextLayout>['header'];
  /**
   * Hides the logged-out "Get started" signup promo (PageFooter). Set on the
   * logged-in surfaces, where a signup CTA is inappropriate and prod doesn't
   * render one.
   */
  hideSignupPromo?: boolean;
}
const AppLayout = ({
  children,
  breadcrumbs,
  description,
  hero,
  title,
  header,
  hideSignupPromo = false
}: AppLayoutProps) => <SfNextLayout header={header} responsiveSection={{
  width: 'full-bleed',
  mt: 0,
  gutter: 'none'
}}>
    <Head>
      <title>{title && `${title} | Stitch Fix`}</title>
      {/* @TODO [SHOPC-1348]: Remove robots meta tag once app is ready  */}
      <meta name="robots" content="noindex, nofollow" />
      {description && <meta name="description" content={description} />}
    </Head>
    {breadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
    {hero && <PageHero {...hero} />}
    <ResponsiveSection width="xxlarge"
  // temporarily using dangerouslySetClassName until kore-fe implements a fix for responsive padding within responsiveSection
  // https://stitchfix.slack.com/archives/CLPDWN5FC/p1660165346027809
  dangerouslySetClassName={styles.temporary_responsive_section_spacing} // eslint-disable-line @stitch-fix/no-dangerous-mode-react-styling-props
  px={1}>
      {children}
    </ResponsiveSection>
    {!hideSignupPromo && <PageFooter />}
  </SfNextLayout>;
export default AppLayout;